import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PublicLandingPage from './PublicLandingPage';
import HelpCenter from './HelpCenter';
import { useSessionTracking } from '../hooks/useSessionTracking';
import { getMainAppOrigin } from '../utils/appOrigin';

// Ensure this file is treated as a module
export { };

const SubdomainRouter: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubdomain, setIsSubdomain] = useState(false);
  const [isSupportSubdomain, setIsSupportSubdomain] = useState(false);
  const [subdomain, setSubdomain] = useState<string>('');
  const [slug, setSlug] = useState<string>('');

  // Track session activity
  useSessionTracking();

  const checkForSubdomain = useCallback(() => {
    const hostname = window.location.hostname;
    const urlParams = new URLSearchParams(window.location.search);
    const pathname = location.pathname;

    // Check for support subdomain (support.scubamail.io)
    // Development: localhost with ?subdomain=support
    // Production: support.scubamail.io
    const devSubdomain = urlParams.get('subdomain');

    // Debug logging
    console.log('SubdomainRouter - Checking:', { hostname, pathname, devSubdomain });

    // Check for support subdomain - be more specific
    const isSupportSubdomainCheck =
      (hostname.includes('localhost') && devSubdomain === 'support') ||
      hostname === 'support.scubamail.io' ||
      hostname.endsWith('.support.scubamail.io') ||
      (hostname.includes('support') && hostname.includes('scubamail.io') && !hostname.includes('app'));

    console.log('SubdomainRouter - Support check result:', isSupportSubdomainCheck);

    if (isSupportSubdomainCheck) {
      console.log('SubdomainRouter - Support subdomain detected:', hostname);
      // Immediately redirect /login and /signup to main app domain (before any state updates)
      if (pathname === '/login' || pathname === '/signup') {
        console.log('SubdomainRouter - Redirecting login/signup to main app');
        window.location.replace(`${getMainAppOrigin()}${pathname}`);
        return;
      }

      console.log('SubdomainRouter - Setting support subdomain state');
      setIsSupportSubdomain(true);
      setIsSubdomain(false);
      return;
    }

    // Check for development mode subdomain parameter (for landing pages)
    const devSlug = urlParams.get('slug');

    if (devSubdomain && hostname.includes('localhost')) {
      // Development mode: use URL parameters
      setSubdomain(devSubdomain);
      setSlug(devSlug || 'home');
      setIsSubdomain(true);
      setIsSupportSubdomain(false);
      return;
    }

    // Check if this is a real subdomain request (production) for landing pages
    if (hostname.includes('sm.page')) {
      const parts = hostname.split('.');

      // If it's a subdomain (not the main domain)
      if (parts.length > 2) {
        const subdomainName = parts[0];

        // Extract slug from pathname
        const pathParts = location.pathname.split('/').filter(part => part);
        const pageSlug = pathParts[0] || 'home';

        setSubdomain(subdomainName);
        setSlug(pageSlug);
        setIsSubdomain(true);
        setIsSupportSubdomain(false);
        return;
      }
    }

    // Not a subdomain request
    setIsSubdomain(false);
    setIsSupportSubdomain(false);
  }, [location.pathname]);

  // Immediate redirect check for /login and /signup on support subdomain
  useEffect(() => {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    const isSupportSubdomain = hostname === 'support.scubamail.io' ||
      hostname.endsWith('.support.scubamail.io') ||
      (hostname.includes('localhost') &&
        new URLSearchParams(window.location.search).get('subdomain') === 'support');

    console.log('SubdomainRouter - Immediate check:', { hostname, pathname, isSupportSubdomain });

    if (isSupportSubdomain && (pathname === '/login' || pathname === '/signup')) {
      window.location.replace(`${getMainAppOrigin()}${pathname}`);
    }
  }, []);

  useEffect(() => {
    checkForSubdomain();
  }, [checkForSubdomain]);

  // If this is the support subdomain, render the help center
  if (isSupportSubdomain) {
    console.log('SubdomainRouter - Rendering HelpCenter');
    return <HelpCenter />;
  }

  // If this is a subdomain request, render the public landing page
  if (isSubdomain) {
    return <PublicLandingPage subdomain={subdomain} slug={slug} />;
  }

  // Not a subdomain request, let the main router handle it
  return null;
};

export default SubdomainRouter;
